// ============================================================================
// FEE REVISIONS HOOK — useSupabaseFeeRevisions
// ============================================================================
// Table:   fee_revisions
// Follows: useSupabase.js conventions
//
// CRUD for fee revision records. A fee revision captures the old/new total
// fee, reason, and effective date. Used by InvoicesPage to determine if
// recalculation preview is needed before issuing an invoice.
//
// VISIBILITY:
//   SELECT — all authenticated users
//   INSERT/UPDATE — admin/admin-pa/supervisor (enforced client-side)
//
// delta_fee is a generated column (new_total_fee - old_total_fee).
// ============================================================================

import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError, normalizeId } from './useSupabase';

// ============================================================================
// SESSION GUARD
// ============================================================================
const requireSession = async () => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw new Error('Authentication error: ' + sessionError.message);
  if (!session || !session.user) {
    throw new Error(
      'Please sign in to perform this action. ' +
      'Note: The "switch user" feature only changes the UI view — ' +
      'it does not change your database permissions.'
    );
  }
  return session.user.id;
};

// ============================================================================
// DB → APP FIELD MAPPING
// ============================================================================
const mapRow = (r) => ({
  id:            normalizeId(r.id),
  projectId:     normalizeId(r.project_id),
  effectiveDate: r.effective_date ?? null,
  oldTotalFee:   Number(r.old_total_fee) || 0,
  newTotalFee:   Number(r.new_total_fee) || 0,
  deltaFee:      Number(r.delta_fee) || 0,
  reason:        r.reason ?? '',
  currency:      r.currency ?? 'INR',
  createdBy:     r.created_by ?? null,
  createdAt:     r.created_at ?? null,
  updatedAt:     r.updated_at ?? null,
  deletedAt:     r.deleted_at ?? null,
});

// ============================================================================
// HOOK
// ============================================================================
export const useSupabaseFeeRevisions = () => {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);

  // ------------------------------------------------------------------
  // FETCH — all revisions for a project, ordered by effective date desc
  // ------------------------------------------------------------------
  const fetchRevisions = useCallback(async (projectId) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('fee_revisions')
        .select('id, project_id, effective_date, old_total_fee, new_total_fee, reason, currency, created_by, created_at, deleted_at')
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('effective_date', { ascending: false });

      if (error) throw error;
      setRevisions((data || []).map(mapRow));
    } catch (err) {
      console.error('[FEE_REVISIONS] fetch error:', err);
      setRevisions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // CREATE — record a fee revision
  // ------------------------------------------------------------------
  const createRevision = useCallback(async ({ projectId, effectiveDate, oldTotalFee, newTotalFee, reason, currency }) => {
    try {
      const authUserId = await requireSession();

      const { data, error } = await supabase
        .from('fee_revisions')
        .insert([{
          project_id:     projectId,
          effective_date: effectiveDate || new Date().toISOString().slice(0, 10),
          old_total_fee:  Number(oldTotalFee) || 0,
          new_total_fee:  Number(newTotalFee) || 0,
          reason:         reason || '',
          currency:       currency || 'INR',
          created_by:     authUserId,
        }])
        .select()
        .single();

      if (error) throw error;

      const mapped = mapRow(data);
      setRevisions(prev => [mapped, ...prev]);
      return { success: true, data: mapped };
    } catch (err) {
      console.error('[FEE_REVISIONS] create error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'fee revision' }) };
    }
  }, []);

  // ------------------------------------------------------------------
  // GET LATEST REVISION — returns the most recent revision for a project
  // (from local state, no additional fetch)
  // ------------------------------------------------------------------
  const getLatestRevision = useCallback(() => {
    if (revisions.length === 0) return null;
    return revisions[0]; // Already sorted desc by effective_date
  }, [revisions]);

  // ------------------------------------------------------------------
  // GET REVISIONS AFTER DATE — useful for checking if fee changed
  // since last issued invoice
  // ------------------------------------------------------------------
  const getRevisionsAfterDate = useCallback((dateStr) => {
    if (!dateStr) return revisions.filter(r => !r.deletedAt);
    return revisions.filter(r => !r.deletedAt && r.effectiveDate > dateStr);
  }, [revisions]);

  // ------------------------------------------------------------------
  // SOFT DELETE — admin only
  // ------------------------------------------------------------------
  const deleteRevision = useCallback(async (revisionId) => {
    try {
      await requireSession();
      const idValue = normalizeId(revisionId);

      const { error } = await supabase
        .from('fee_revisions')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', idValue)
        .is('deleted_at', null);

      if (error) throw error;

      setRevisions(prev => prev.filter(r => r.id !== idValue));
      return { success: true };
    } catch (err) {
      console.error('[FEE_REVISIONS] delete error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'fee revision' }) };
    }
  }, []);

  return {
    revisions,
    loading,
    fetchRevisions,
    createRevision,
    getLatestRevision,
    getRevisionsAfterDate,
    deleteRevision,
  };
};
